import type { Option } from '@common/types/form-element.type';
import type { PaymentElement } from '@integrations/payment-methods';
import type { PaymentFormWidgetData } from '@web/widgets/payment-form/type';

import { ChangeDetectorRef, Component, inject, Input, type OnInit, ViewEncapsulation } from '@angular/core';

import { paymentsList } from '@common/constants/payment-form.const';
import { EButtonStyleType, EFormInputType, ESize } from '@common/enums';
import * as typeGuards from '@common/type-guards/form-elements.type-guards';
import { CountryISO, PhoneNumberFormat, SearchCountryField, NgxIntlTelInputModule } from '@moddi3/ngx-intl-tel-input';

import type { WebLegacyWidgetEntity } from '@web/types';
import {
    BUTTON_SIZE_CLASS_MAP,
    BUTTON_TYPE_CLASS_MAP,
    DEFAULT_BUTTON_CLASS,
} from '@web/widgets/common/base/base-button/base-button.const';
import { type FormElement } from '@common/types/form-element.type';
import { CountryISOService } from '@common/services/REST/country-iso.http.service';
import { ProductType } from '@web/widgets/payment-form/type/payment-form-widget.type';
import type { SafeHtml } from '@angular/platform-browser';
import { DomSanitizer } from '@angular/platform-browser';
import { SpSafeHtmlPipe } from '@common/pipes/safe-html.pipe';
import { RemovePTags } from '@common/pipes/remove-p-tag.pipe';
import { TranslateModule } from '@ngx-translate/core';

import { FormsModule } from '@angular/forms';

import type { FormCssVars } from '@sites/entity-core/src/widget/form/styles/form.css-vars';
import {
    getFormAlignCssVars,
    getFormButtonCssClasses,
    getFormButtonCssVars,
    getFormInputCssClasses,
    getFormInputCssVars,
    getFormRootCssClasses,
    getFormSizeCssVars,
} from '@sites/entity-core/src/widget/form/styles/form.css-vars';
import {
    getPaymentDescriptionCssVars,
    getPaymentModalCssVars,
} from '@sites/entity-core/src/widget/payment-form/styles/payment-form.css-vars';
import { SpIntlTelInputA11yDirective } from '@common/directives/intl-tel-input-a11y.directive';
import { colorCss } from '@web/widgets/common/css-vars/css-vars.utils';

// TODO: refactor this component into forms/signals
@Component({
    selector: 'web-payment-form-modal-preview',
    templateUrl: './payment-form-modal-preview.component.html',
    styleUrls: ['./payment-form-modal-preview.component.less'],
    encapsulation: ViewEncapsulation.None,
    imports: [
        FormsModule,
        NgxIntlTelInputModule,
        SpIntlTelInputA11yDirective,
        TranslateModule,
        RemovePTags,
        SpSafeHtmlPipe,
    ],
})
export class PaymentFormModalComponent implements OnInit {
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    protected readonly sanitizer = inject(DomSanitizer);

    @Input() public widget: WebLegacyWidgetEntity<PaymentFormWidgetData>;

    // --- CSS custom properties (self-computed from the widget data) ---

    public get modalCssVars(): FormCssVars {
        return getPaymentModalCssVars(this.inputSettings, colorCss);
    }

    public get rootCssVars(): FormCssVars {
        return {
            ...getFormInputCssVars(this.inputSettings, colorCss),
            ...getFormSizeCssVars(this.inputSettings),
            ...getFormAlignCssVars(this.inputSettings),
            ...getPaymentDescriptionCssVars(this.inputSettings, colorCss),
        };
    }

    public get rootCssClasses(): string {
        return getFormRootCssClasses(this.inputSettings);
    }

    public get fieldCssClasses(): string {
        return getFormInputCssClasses(this.inputSettings);
    }

    public get buttonCssVars(): FormCssVars {
        return getFormButtonCssVars(this.formButtonSettings, this.inputSettings.formStyleType, colorCss);
    }

    public readonly typeGuards = typeGuards;
    public readonly formInputType = EFormInputType;
    public readonly SearchCountryField = SearchCountryField;
    public readonly PhoneNumberFormat = PhoneNumberFormat;

    public CountryISO = CountryISO.Ukraine;
    public excludeCountries = [CountryISO.Russia];

    private countryISOService: CountryISOService = inject(CountryISOService);

    public ngOnInit(): void {
        this.getCountryISO();
    }

    public getSelected(options: Option[]) {
        return !options.some((el) => el.selected);
    }

    public getButtonStyles(formButtonSettings: PaymentFormWidgetData['formButtonSettings']): string {
        const base = `${DEFAULT_BUTTON_CLASS} ${BUTTON_SIZE_CLASS_MAP[formButtonSettings.size ? formButtonSettings.size : ESize.M]} form-button`;
        const themed = getFormButtonCssClasses(formButtonSettings);
        const type =
            formButtonSettings.styleType === EButtonStyleType.custom
                ? ''
                : BUTTON_TYPE_CLASS_MAP[formButtonSettings.styleType];

        return `${base} ${type} ${themed}`;
    }

    public getIcon(icon: string): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(icon);
    }

    public getPaymentElementLabel(paymentElementType: PaymentElement['type']): string {
        return paymentsList[paymentElementType].label;
    }

    public get inputSettings(): PaymentFormWidgetData['inputSettings'] {
        return this.widget.data.inputSettings;
    }

    public get formButtonSettings(): PaymentFormWidgetData['formButtonSettings'] {
        return this.widget.data.formButtonSettings;
    }

    public get paymentElement(): PaymentElement {
        return this.widget.data.paymentElements[0];
    }

    public get paymentElements(): PaymentElement[] {
        return this.widget.data.paymentElements;
    }

    public get activePaymentElements(): PaymentElement[] {
        return this.widget.data.paymentElements.filter((el: PaymentElement) => el.isActive);
    }

    public get elements(): FormElement[] {
        return this.widget.data.elements;
    }

    public get productType(): PaymentFormWidgetData['productType'] {
        return this.widget.data.productType;
    }

    public get isProductTypeEduCourse(): boolean {
        return this.productType === ProductType.eduCourse;
    }

    private getCountryISO() {
        this.countryISOService.getCountryISO().subscribe((data) => {
            this.CountryISO = data.country_code.toLowerCase();
            this.changeDetectorRef.detectChanges();
        });
    }
}
